import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, DestroyRef, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

import { THEME_OPTIONS } from '../core/theme.tokens';
import { isThemePreference, ThemeMode, ThemePreference } from '../core/theme.types';

@Injectable({
  providedIn: 'root',
})
export class ThemeStore {
  readonly #options = inject(THEME_OPTIONS);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #destroyRef = inject(DestroyRef);
  readonly #document = inject(DOCUMENT);

  readonly #isBrowser = isPlatformBrowser(this.#platformId);
  readonly #preference = signal<ThemePreference>(this.#options.defaultPreference);
  readonly #systemTheme = signal<ThemeMode>('light');
  readonly #initialized = signal(false);

  readonly themePreference = computed(() => this.#preference());
  readonly resolvedTheme = computed<ThemeMode>(() => {
    const preference = this.#preference();
    return preference === 'system' ? this.#systemTheme() : preference;
  });
  readonly isDark = computed(() => this.resolvedTheme() === 'dark');

  constructor() {
    this.#systemTheme.set(this.#readSystemTheme());

    if (this.#options.syncWithSystem) {
      this.#registerSystemThemeListener();
    }

    this.#restorePreference();

    effect(() => {
      if (!this.#initialized()) {
        return;
      }

      this.#savePreference(this.themePreference());
    });
  }

  setPreference(preference: ThemePreference): void {
    this.#preference.set(preference);
  }

  setTheme(theme: ThemeMode): void {
    this.setPreference(theme);
  }

  toggleTheme(): void {
    this.setTheme(this.resolvedTheme() === 'dark' ? 'light' : 'dark');
  }

  resetToSystem(): void {
    this.setPreference('system');
  }

  #restorePreference(): void {
    const storedPreference = this.#readPreference();
    this.#preference.set(storedPreference ?? this.#options.defaultPreference);
    this.#initialized.set(true);
  }

  #readPreference(): ThemePreference | null {
    if (!this.#isBrowser) {
      return null;
    }

    try {
      const value = localStorage.getItem(this.#options.storageKey);
      return isThemePreference(value) ? value : null;
    } catch {
      return null;
    }
  }

  #savePreference(preference: ThemePreference): void {
    if (!this.#isBrowser) {
      return;
    }

    try {
      if (!this.#options.persist) {
        localStorage.removeItem(this.#options.storageKey);
        return;
      }

      localStorage.setItem(this.#options.storageKey, preference);
    } catch {
      // Ignore storage failures.
    }
  }

  #readSystemTheme(): ThemeMode {
    const mediaQuery = this.#getSystemThemeMediaQuery();

    if (!mediaQuery) {
      return 'light';
    }

    return mediaQuery.matches ? 'dark' : 'light';
  }

  #registerSystemThemeListener(): void {
    const mediaQuery = this.#getSystemThemeMediaQuery();

    if (!mediaQuery) {
      return;
    }
    const listener = (event: MediaQueryListEvent): void => {
      this.#systemTheme.set(event.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', listener);
    this.#destroyRef.onDestroy(() => mediaQuery.removeEventListener('change', listener));
  }

  #getSystemThemeMediaQuery(): MediaQueryList | null {
    const defaultView = this.#document?.defaultView;

    if (!this.#isBrowser || typeof defaultView?.matchMedia !== 'function') {
      return null;
    }

    return defaultView.matchMedia('(prefers-color-scheme: dark)');
  }
}
